import axios from 'axios';
import { renderSnackbar, getEnv } from '../utils';
import {
  clearUserSessionFromStorage,
  setUserSessionToStorage,
} from 'utils/helper';
import { isTokenAvailable } from 'utils/validateToken';
import i18next from 'i18next';

const workingEnv = getEnv();

export const BACKEND_URL =
  workingEnv === 'production'
    ? 'https://admin55.sdsmanager.com'
    : workingEnv == 'rc'
    ? 'https://rc-admin55.sdsmanager.com'
    : workingEnv == 'staging'
    ? 'https://staging-admin55.sdsmanager.com'
    : 'http://localhost:8000';

export const BASE_API_URL = `${BACKEND_URL}/api`;

export const EXCLUDE_REDIRECT_LOGIN_ERROR_CODES = ['IP_ADDRESS_FORBIDDEN'];

let expiredTokenDialog = false;
let isRefreshing = false;
let waitingQueue = [];
const accessToken = localStorage.getItem('access_token');

const axiosAuthoringInstance = axios.create({
  baseURL: BASE_API_URL,
  timeout: 50000,
  headers: {
    Authorization: accessToken ? 'JWT ' + accessToken : null,
    'Content-Type': 'application/json',
    accept: 'application/json',
  },
});

const processQueue = err => {
  waitingQueue.forEach(item => {
    if (err) item.reject(err);
    else item.resolve();
  });
};

export const handleRedirectLogin = () => {
  const pathName = window.location.pathname;
  const redirectUrl =
    pathName !== '/' ? `/login/?redirectPath=${pathName}` : '/login/';
  clearUserSessionFromStorage();
  window.location.href = redirectUrl;
};

const notifySnackbar = error => {
  if (
    typeof error.response?.data?.error_message == 'string' &&
    !!error.response.data.error_message
  )
    renderSnackbar([error.response.data.error_message]);

  if (typeof error.response?.data?.error_message == 'object') {
    const res = [];
    Object.entries(error.response.data.error_message).map(([key, value]) => {
      res.push(
        `<span style="text-transform: capitalize">${key}</span>: ${value.join(
          ', '
        )}`
      );
    });
    renderSnackbar(res);
  }
  return Promise.reject(error);
};

axiosAuthoringInstance.interceptors.request.use(config => {
  let accessToken = localStorage.getItem('access_token');
  config.headers['Authorization'] = accessToken ? `JWT ${accessToken}` : null;
  return config;
});

axiosAuthoringInstance.interceptors.response.use(
  response => response,
  error => {
    // Handle request time out
    if (!error.response) {
      renderSnackbar([i18next.t('common:request_time_out')]);
      return Promise.reject({ response: { status: 408 } });
    }

    const originalRequest = error.config;

    // Prevent infinite loops
    if (originalRequest.url.includes('/auth/refresh-token')) {
      handleRedirectLogin();
      return Promise.reject(error);
    }

    if (error.response.status === 401) {
      const accessToken = localStorage.getItem('access_token') ?? '';
      const authorizationHeader = accessToken ? 'JWT ' + accessToken : null;
      // Reload api when refresh token has just finished
      if (authorizationHeader !== originalRequest.headers['Authorization']) {
        return axiosAuthoringInstance(originalRequest);
      }

      // Waiting queue push if refresh-token api is pending
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          waitingQueue.push({ resolve, reject });
        })
          .then(() => {
            return axiosAuthoringInstance(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken && isTokenAvailable(refreshToken)) {
        // Refresh token existed and in JWT format
        isRefreshing = true;
        return axiosAuthoringInstance
          .post('/auth/refresh-token/', { token: refreshToken })
          .then(response => {
            setUserSessionToStorage(
              undefined,
              refreshToken,
              response.data.token
            );
            isRefreshing = false;
            processQueue(null);
            waitingQueue = [];
            return axiosAuthoringInstance(originalRequest);
          })
          .catch(err => {
            processQueue(err);
            return Promise.reject(error);
          });
      } else {
        if (!expiredTokenDialog) {
          expiredTokenDialog = true;
          if (
            !EXCLUDE_REDIRECT_LOGIN_ERROR_CODES.includes(
              error.response?.data.error_code
            )
          )
            handleRedirectLogin();
        }
      }

      return Promise.reject(error);
    }

    // Handle snackbar notification
    if (error.response?.data?.error_code !== undefined)
      return notifySnackbar(error);

    return Promise.reject(error);
  }
);

export const get = (path, params) => {
  return axiosAuthoringInstance
    .get(path, { params: params })
    .then(function (response) {
      return response;
    })
    .catch(function (error) {
      return error.response;
    });
};

export const post = (path, data) => {
  return axiosAuthoringInstance
    .post(path, data)
    .then(function (response) {
      return response;
    })
    .catch(function (error) {
      return error.response;
    });
};

export const deleteRequest = path => {
  return axiosAuthoringInstance
    .delete(path)
    .then(function (response) {
      return response;
    })
    .catch(function (error) {
      return error.response;
    });
};

export const update = (path, data) => {
  return axiosAuthoringInstance
    .patch(path, data)
    .then(function (response) {
      return response;
    })
    .catch(function (error) {
      return error.response;
    });
};
export default axiosAuthoringInstance;
